import React, { useMemo } from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';

import { useTheme } from '../contexts/ThemeContext';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler
);

interface StatsGraphProps {
    data: any[];
    dataKeys: { key: string; label: string; color: string }[];
    title?: string;
    interval: 'daily' | 'weekly' | 'monthly';
}

const StatsGraph: React.FC<StatsGraphProps> = ({ data, dataKeys, title, interval }) => {

    const { actualTheme } = useTheme();

    const isDark = actualTheme === 'dark';

    const chartData = useMemo(() => {
        // Sort data by date just in case
        const sortedData = [...data].sort((a, b) => {
            const dateA = new Date(a.startDate || a.date).getTime();
            const dateB = new Date(b.startDate || b.date).getTime();
            return dateA - dateB;
        });

        const labels = sortedData.map(item => {
            const date = new Date(item.startDate || item.date);
            return date.toLocaleDateString(); // Simple formatting, can improve
        });

        const datasets = dataKeys.map(metric => ({
            label: metric.label,
            data: sortedData.map(item => item[metric.key] || 0),
            borderColor: metric.color,
            backgroundColor: metric.color + '20', // Add transparency
            tension: 0.3,
            fill: true,
            pointRadius: 4,
            pointHoverRadius: 6,
        }));

        return {
            labels,
            datasets,
        };
    }, [data, dataKeys, interval]);

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'top' as const,
                labels: {
                    color: isDark ? '#e5e5e5' : '#404040',
                    font: {
                        family: 'Inter, sans-serif',
                        size: 12
                    },
                    usePointStyle: true,
                    boxWidth: 8,
                }
            },
            title: {
                display: !!title,
                text: title,
                color: isDark ? '#e5e5e5' : '#171717',
                font: {
                    size: 16,
                    weight: 'bold' as const,
                }
            },
            tooltip: {
                mode: 'index' as const,
                intersect: false,
                backgroundColor: isDark ? '#1f2937' : '#ffffff',
                titleColor: isDark ? '#f3f4f6' : '#111827',
                bodyColor: isDark ? '#e5e5e5' : '#4b5563',
                borderColor: isDark ? '#374151' : '#e5e7eb',
                borderWidth: 1,
                padding: 10,
                boxPadding: 4,
            }
        },
        scales: {
            x: {
                grid: {
                    color: isDark ? '#374151' : '#e5e7eb',
                    drawBorder: false,
                },
                ticks: {
                    color: isDark ? '#9ca3af' : '#6b7280',
                }
            },
            y: {
                grid: {
                    color: isDark ? '#374151' : '#e5e7eb',
                    drawBorder: false,
                    borderDash: [4, 4],
                },
                ticks: {
                    color: isDark ? '#9ca3af' : '#6b7280',
                },
                beginAtZero: true,
            },
        },
        interaction: {
            mode: 'nearest' as const,
            axis: 'x' as const,
            intersect: false
        }
    };

    return (
        <div className="w-full h-80 relative">
            <Line options={options} data={chartData} />
        </div>
    );
};

export default StatsGraph;
